import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Edit2, X, ExternalLink, Key } from 'lucide-react'
import { useAuth } from '../../../../context/AuthContext'
import AdvertiserEditor from './AdvertiserEditor'
import LinkAdsToEntityModal from '../shared/LinkAdsToEntityModal'
import '../SponsorManager.css'

export default function AdvertiserDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { api } = useAuth()

  const [advertiser, setAdvertiser] = useState(null)
  const [username, setUsername] = useState('')
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [status, setStatus] = useState('')
  const [linkedAds, setLinkedAds] = useState([])
  const [linkedAdsLoading, setLinkedAdsLoading] = useState(true)

  // TBTV tags
  const [tbtvTags, setTbtvTags] = useState([])
  const [allTbtvTags, setAllTbtvTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [showTagSugg, setShowTagSugg] = useState(false)

  // Password change
  const [showPwForm, setShowPwForm] = useState(false)
  const [pwCurrent, setPwCurrent] = useState('')
  const [pwNew, setPwNew] = useState('')
  const [pwSaving, setPwSaving] = useState(false)

  // Link ads modal
  const [showLinkAds, setShowLinkAds] = useState(false)

  const tagInputRef = useRef(null)

  const loadAdvertiser = useCallback(async () => {
    try {
      const data = await api(`/sponsors/advertisers/${id}`)
      setAdvertiser(data.advertiser)
      setUsername(data.username || '')
      setTbtvTags((data.tbtvTags || []).map(t => t.name))
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }, [api, id])

  const loadLinkedAds = useCallback(async () => {
    setLinkedAdsLoading(true)
    try {
      const data = await api(`/sponsors/advertisers/${id}/ads`)
      setLinkedAds(data.ads || [])
    } catch (e) {
      setStatus(`Linked-ads error: ${e.message}`)
    }
    setLinkedAdsLoading(false)
  }, [api, id])

  const loadAllTags = useCallback(async () => {
    try {
      const data = await api('/tags')
      setAllTbtvTags(data.tags || [])
    } catch {}
  }, [api])

  useEffect(() => {
    setLoading(true)
    Promise.all([loadAdvertiser(), loadAllTags(), loadLinkedAds()]).finally(() => setLoading(false))
  }, [loadAdvertiser, loadAllTags, loadLinkedAds])

  async function handleEditSubmit(body) {
    const res = await api(`/sponsors/advertisers/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
    if (res?.ok === false) {
      throw new Error(res.error?.message || 'AdSpeed rejected the edit')
    }
    setEditing(false)
    setStatus('Advertiser contact updated')
    await loadAdvertiser()
  }

  async function persistTbtvTags(next) {
    try {
      await api(`/sponsors/advertisers/${id}/tbtv-tags`, {
        method: 'PUT',
        body: JSON.stringify({ tags: next }),
      })
      setStatus('Tags saved')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
      await loadAdvertiser()
    }
  }

  function addTag(name) {
    const clean = name.trim().toLowerCase()
    if (!clean || tbtvTags.includes(clean)) return
    const next = [...tbtvTags, clean]
    setTbtvTags(next)
    setTagInput('')
    setShowTagSugg(false)
    persistTbtvTags(next)
  }

  function removeTag(name) {
    const next = tbtvTags.filter(t => t !== name)
    setTbtvTags(next)
    persistTbtvTags(next)
  }

  function handleTagKey(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (tagInput.trim()) addTag(tagInput)
    } else if (e.key === 'Backspace' && !tagInput && tbtvTags.length) {
      removeTag(tbtvTags[tbtvTags.length - 1])
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault()
    if (!pwCurrent || !pwNew) {
      setStatus('Error: Both current and new password are required')
      return
    }
    if (pwNew.length < 8 || !/[a-zA-Z]/.test(pwNew) || !/[0-9]/.test(pwNew)) {
      setStatus('Error: New password needs 8+ chars with at least one letter and one number')
      return
    }
    setPwSaving(true)
    try {
      const res = await api(`/sponsors/advertisers/${id}/change-password`, {
        method: 'POST',
        body: JSON.stringify({ current: pwCurrent, new: pwNew }),
      })
      if (res?.ok === false) throw new Error(res.error?.message || 'AdSpeed rejected the change')
      setStatus('Password changed')
      setShowPwForm(false)
      setPwCurrent('')
      setPwNew('')
    } catch (err) {
      setStatus(`Error: ${err.message}`)
    }
    setPwSaving(false)
  }

  const filteredTagSuggestions = allTbtvTags
    .filter(t => t.toLowerCase().includes(tagInput.toLowerCase()) && !tbtvTags.includes(t))
    .slice(0, 8)

  if (loading) {
    return (
      <div className="sponsors-page">
        <div className="muted">Loading advertiser…</div>
      </div>
    )
  }

  if (!advertiser) {
    return (
      <div className="sponsors-page">
        <button className="sponsors-refresh-btn" onClick={() => navigate('/sponsors/advertisers')}>
          <ArrowLeft size={14} /> Back to advertisers
        </button>
        <div className="sponsors-status error" style={{ marginTop: 12 }}>
          Advertiser {id} not found.
        </div>
      </div>
    )
  }

  const f = (k, K) => advertiser?.[k] ?? advertiser?.[K] ?? null

  const fields = [
    ['id', f('id', 'AdvertiserId') ?? id],
    ['username', username || f('username', 'Username')],
    ['fullname', f('fullname', 'Fullname') ?? f('fullname', 'FullName')],
    ['email', f('email', 'Email')],
    ['company', f('company', 'Company')],
    ['companyurl', f('companyurl', 'CompanyURL') ?? f('companyurl', 'Companyurl')],
    ['status', f('status', 'Status')],
    ['address', f('address', 'Address')],
    ['city', f('city', 'City')],
    ['state', f('state', 'State')],
    ['country', f('country', 'Country')],
    ['postal', f('postal', 'Postal')],
    ['phone', f('phone', 'Phone')],
  ]

  // Linked ads come from the dedicated /ads subresource which queries
  // AS.Ads.getList?advertiser=<id> across all statuses — AdSpeed's
  // getInfo for advertisers does NOT include them (unlike zones/groups).
  const fullname = f('fullname', 'Fullname') ?? f('fullname', 'FullName') ?? username ?? `Advertiser ${id}`
  const adspeedDashboardUrl = `https://advertiser.tenband.tv/Advertisers/show.html?id=${id}`

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <button className="sponsors-refresh-btn" onClick={() => navigate('/sponsors/advertisers')}>
            <ArrowLeft size={14} /> Advertisers
          </button>
          <h1>{fullname}</h1>
        </div>
        <div className="sponsors-header-right">
          <a
            className="sponsors-refresh-btn"
            href={adspeedDashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={14} /> View in AdSpeed
          </a>
          <button className="sponsors-refresh-btn" onClick={loadAdvertiser}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setEditing(true)}>
            <Edit2 size={14} /> Edit contact
          </button>
        </div>
      </div>

      {status && (
        <div className={`sponsors-status ${status.toLowerCase().includes('error') ? 'error' : 'success'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className="zone-detail-grid">
        <div>
          <div className="zone-detail-card">
            <h3>Advertiser properties</h3>
            {fields.map(([k, v]) => (
              <div className="zone-detail-field" key={k}>
                <span className="k">{k}</span>
                <span className="v">{v == null || v === '' ? '—' : String(v)}</span>
              </div>
            ))}
          </div>

          <div className="zone-detail-card" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>
                <Key size={14} style={{ display: 'inline', marginRight: 6 }} />
                Change password
              </h3>
              {!showPwForm && (
                <button
                  className="sponsors-refresh-btn"
                  onClick={() => setShowPwForm(true)}
                  style={{ fontSize: '0.85rem' }}
                >
                  Change…
                </button>
              )}
            </div>
            {showPwForm ? (
              <form onSubmit={handleChangePassword}>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#aaa', marginBottom: 4 }}>
                    Current password
                  </label>
                  <input
                    type="password"
                    value={pwCurrent}
                    onChange={e => setPwCurrent(e.target.value)}
                    className="zones-search"
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#aaa', marginBottom: 4 }}>
                    New password
                  </label>
                  <input
                    type="password"
                    value={pwNew}
                    onChange={e => setPwNew(e.target.value)}
                    className="zones-search"
                    style={{ width: '100%' }}
                  />
                  <div className="muted" style={{ fontSize: '0.7rem', marginTop: 4 }}>
                    8+ chars with at least one letter and one number.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => { setShowPwForm(false); setPwCurrent(''); setPwNew('') }} className="sponsors-refresh-btn">
                    Cancel
                  </button>
                  <button type="submit" disabled={pwSaving} className="btn btn-primary">
                    {pwSaving ? 'Saving…' : 'Change password'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="muted" style={{ fontSize: '0.78rem' }}>
                Requires the advertiser's current password — admin cannot reset blindly via AdSpeed's API.
              </div>
            )}
          </div>

          <div className="zone-detail-card" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>
                Linked ads {linkedAdsLoading ? '' : `(${linkedAds.length})`}
              </h3>
              <button
                className="sponsors-refresh-btn"
                onClick={() => setShowLinkAds(true)}
                style={{ fontSize: '0.85rem' }}
              >
                + Link ads
              </button>
            </div>
            {linkedAdsLoading ? (
              <div className="muted" style={{ fontSize: '0.85rem' }}>Loading linked ads…</div>
            ) : linkedAds.length === 0 ? (
              <div className="muted" style={{ fontSize: '0.85rem' }}>
                No ads linked to this advertiser. Use "Link ads" to add some.
              </div>
            ) : (
              <table className="zones-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedAds.map(a => {
                    const aid = a.id ?? a.AdId
                    return (
                      <tr
                        key={aid}
                        onClick={() => navigate(`/sponsors/ads/${aid}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{aid}</td>
                        <td>{a.name ?? a.Name}</td>
                        <td>{a.status ?? a.Status}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div>
          <div className="zone-detail-card">
            <h3>TBTV tags</h3>
            <div style={{ marginBottom: 10 }}>
              {tbtvTags.length === 0 && (
                <span className="muted" style={{ fontSize: '0.85rem' }}>No tags applied</span>
              )}
              {tbtvTags.map(t => (
                <span className="tag-chip" key={t}>
                  {t}
                  <button onClick={() => removeTag(t)} aria-label={`Remove ${t}`}>
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
            <div className="tag-input-wrap">
              <input
                ref={tagInputRef}
                className="tag-input"
                placeholder="Add tag…"
                value={tagInput}
                onChange={e => { setTagInput(e.target.value); setShowTagSugg(true) }}
                onKeyDown={handleTagKey}
                onFocus={() => setShowTagSugg(true)}
                onBlur={() => setTimeout(() => setShowTagSugg(false), 150)}
              />
              {showTagSugg && filteredTagSuggestions.length > 0 && (
                <div className="tag-suggestions">
                  {filteredTagSuggestions.map(s => (
                    <div key={s} className="tag-suggestion" onMouseDown={() => addTag(s)}>
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="muted" style={{ fontSize: '0.78rem', marginTop: 8 }}>
              Shared tag system — same tag namespace as content, zones, ads, groups.
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <AdvertiserEditor
          initial={{ ...advertiser, username }}
          onClose={() => setEditing(false)}
          onSubmit={handleEditSubmit}
        />
      )}

      {showLinkAds && (
        <LinkAdsToEntityModal
          kind="advertiser"
          entityId={id}
          entityName={username}
          alreadyLinkedAdIds={new Set(linkedAds.map(a => String(a.id ?? a.AdId)))}
          /* linkedAds source above is the /ads subresource — already includes ads across all statuses */
          onClose={() => setShowLinkAds(false)}
          onComplete={async (ok, fail, lastErr) => {
            setShowLinkAds(false)
            setStatus(fail === 0
              ? `Linked ${ok} ad(s) to this advertiser.`
              : `Linked ${ok}, ${fail} failed. ${lastErr}`)
            // Bust the linked-ads cache so newly linked ads appear.
            await api(`/sponsors/cache/bust?prefix=advertiser:${id}:ads`, { method: 'POST' }).catch(() => {})
            loadAdvertiser()
            loadLinkedAds()
          }}
        />
      )}
    </div>
  )
}

